import PropTypes from "prop-types";
import InfoBlockValueAttribute from "../band_page/InfoBlockValueAttribute";

const EventMeta = ({ data }) => {
  // Format date to a more readable format
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("sv-SE", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getPerformerItems = () => {
    if (Array.isArray(data?.event?.performers)) {
      return data.event.performers
        .map((performer) => ({
          name: performer?.band_name || performer?.name || "",
          slug: performer?.band_slug || null,
        }))
        .filter((performer) => performer.name);
    }

    if (Array.isArray(data?.event?.performers_data)) {
      return data.event.performers_data
        .map((performer) => ({
          name:
            performer?.name ||
            performer?.performer_name ||
            performer?.performer ||
            "",
          slug: performer?.band_slug || performer?.slug || null,
        }))
        .filter((performer) => performer.name);
    }

    if (typeof data?.event?.performers === "string") {
      return data.event.performers
        .split(",")
        .map((name) => ({ name: name.trim(), slug: null }))
        .filter((performer) => performer.name);
    }

    return [];
  };

  const performerItems = getPerformerItems();
  const postedByType = (data?.event?.posted_by_type || "").toLowerCase();
  const isPostedByOrganizer = [
    "org",
    "organizer",
    "organiser",
    "organization",
  ].includes(postedByType);
  const organizerName =
    data?.event?.posted_by_name ||
    data?.event?.posted_by ||
    data?.event?.organizer ||
    data?.event?.organiser ||
    data?.event?.posted_by?.name ||
    data?.event?.organizer?.name ||
    data?.event?.organiser?.name;
  const organizerSlug =
    data?.event?.posted_by_slug ||
    data?.event?.organizer_slug ||
    data?.event?.organiser_slug ||
    data?.event?.posted_by?.slug ||
    data?.event?.organizer?.slug ||
    data?.event?.organiser?.slug;

  return (
    <div className="bg-[#131313] border border-white/10 rounded-2xl w-full p-6 my-6 shadow-lg">
      <h4 className="text-lg font-extrabold text-white uppercase tracking-wide mb-5">
        Evenemangsinformation
      </h4>
      <div className="flex flex-col gap-1">
        {/* Evenemang */}
        <div className="flex justify-between items-center py-3.5 border-b border-white/5">
          <span className="text-xs font-bold text-stone-400 uppercase tracking-wider">Evenemang</span>
          <span className="text-sm font-extrabold text-white">{data.event.name}</span>
        </div>

        {/* Datum och tid */}
        <div className="flex justify-between items-center py-3.5 border-b border-white/5">
          <span className="text-xs font-bold text-stone-400 uppercase tracking-wider">Datum och tid</span>
          <span className="text-sm font-extrabold text-white">{formatDate(data.event.date)}</span>
        </div>

        {/* Plats */}
        <div className="flex justify-between items-center py-3.5 border-b border-white/5">
          <span className="text-xs font-bold text-stone-400 uppercase tracking-wider">Plats</span>
          <span className="text-sm font-extrabold text-white">{data.event.location}</span>
        </div>

        {/* Upplagd av */}
        {data.event.posted_by && (
          <div className="flex justify-between items-center py-3.5 border-b border-white/5">
            <span className="text-xs font-bold text-stone-400 uppercase tracking-wider">Upplagd av</span>
            <span className="text-sm font-extrabold text-white">{data.event.posted_by}</span>
          </div>
        )}

        {/* Arrangör (social or typed) */}
        {organizerName && (
          <div className="flex justify-between items-center py-3.5 border-b border-white/5">
            <span className="text-xs font-bold text-stone-400 uppercase tracking-wider">Arrangör</span>
            <span className="text-sm font-extrabold text-white">
              {organizerSlug ? (
                <a
                  href={`/orgs/${organizerSlug}`}
                  className="text-white hover:text-[#0047ff] transition-colors"
                >
                  {organizerName}
                </a>
              ) : (
                organizerName
              )}
            </span>
          </div>
        )}

        {/* Artister/Band */}
        {performerItems.length > 0 && (
          <div className="flex justify-between items-center py-3.5 border-b border-white/5">
            <span className="text-xs font-bold text-stone-400 uppercase tracking-wider">Artister/Band</span>
            <span className="text-sm font-extrabold text-white flex flex-wrap gap-1">
              {performerItems.map((performer, index) => (
                <span key={`${performer.name}-${index}`}>
                  {performer.slug ? (
                    <a
                      href={`/music/${performer.slug}`}
                      className="text-white hover:text-[#0047ff] transition-colors font-extrabold"
                    >
                      {performer.name}
                    </a>
                  ) : (
                    <span className="text-white font-extrabold">{performer.name}</span>
                  )}
                  {index < performerItems.length - 1 && <span className="text-stone-500">, </span>}
                </span>
              ))}
            </span>
          </div>
        )}

        {/* Fallback Arrangör */}
        {!organizerName && (data.event.organiser || data.event.organizer) && (
          <div className="flex justify-between items-center py-3.5 border-b border-white/5">
            <span className="text-xs font-bold text-stone-400 uppercase tracking-wider">Arrangör</span>
            <span className="text-sm font-extrabold text-white">
              {data.event.organiser || data.event.organizer}
            </span>
          </div>
        )}

        {/* Pris */}
        {data.event.price && (
          <div className="flex justify-between items-center py-3.5 border-b border-white/5">
            <span className="text-xs font-bold text-stone-400 uppercase tracking-wider">Pris</span>
            <span className="text-sm font-extrabold text-white">{data.event.price} kr</span>
          </div>
        )}

        {/* Kategori */}
        {data.event.category && (
          <div className="flex justify-between items-center py-3.5 border-b border-white/5">
            <span className="text-xs font-bold text-stone-400 uppercase tracking-wider">Kategori</span>
            <span className="text-sm font-extrabold text-white capitalize">{data.event.category}</span>
          </div>
        )}

        {/* Webbplats */}
        {data.event.website && (
          <div className="flex justify-between items-center py-3.5">
            <span className="text-xs font-bold text-stone-400 uppercase tracking-wider">Webbplats</span>
            <span className="text-sm font-extrabold text-[#0047ff] hover:underline">
              <a href={data.event.website} target="_blank" rel="noopener noreferrer">
                Besök hemsida
              </a>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

EventMeta.propTypes = {
  data: PropTypes.shape({
    event: PropTypes.shape({
      name: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
      location: PropTypes.string.isRequired,
      posted_by: PropTypes.string,
      posted_by_name: PropTypes.string,
      posted_by_type: PropTypes.string,
      posted_by_slug: PropTypes.string,
      performers: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            name: PropTypes.string,
            band_slug: PropTypes.string,
            band_name: PropTypes.string,
          }),
        ),
      ]),
      performers_data: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          name: PropTypes.string,
          performer_name: PropTypes.string,
          performer: PropTypes.string,
          band_slug: PropTypes.string,
          band_name: PropTypes.string,
          slug: PropTypes.string,
        }),
      ),
      organiser: PropTypes.string,
      organiser_slug: PropTypes.string,
      organizer: PropTypes.string,
      organizer_slug: PropTypes.string,
      price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      category: PropTypes.string,
      website: PropTypes.string,
    }).isRequired,
  }).isRequired,
};

export default EventMeta;
